'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { setPosthogAnalyticsConsent } from '../lib/posthog-lazy';
import { clearStoredClickIds } from '../lib/clickIds';
import { clearStoredAttribution } from '../lib/attribution';
import { updateGoogleConsent } from '../components/GoogleAnalytics/GoogleAnalytics';
import { updateMetaConsent } from '../components/MetaPixel/MetaPixel';
import { ESSENTIAL_COOKIES } from '../../constants';

type ConsentStatus = 'pending' | 'saved';

export interface ConsentCategories {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentContextType {
  status: ConsentStatus;
  categories: ConsentCategories;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (next: Partial<Omit<ConsentCategories, 'necessary'>>) => void;
  resetConsent: () => void;
}

const STORAGE_KEY = 'cookie-consent-v2';
const LEGACY_STORAGE_KEY = 'cookie-consent';

const DEFAULT_CATEGORIES: ConsentCategories = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const CookieConsentContext = createContext<CookieConsentContextType>({
  status: 'pending',
  categories: DEFAULT_CATEGORIES,
  acceptAll: () => {},
  rejectAll: () => {},
  savePreferences: () => {},
  resetConsent: () => {},
});

function clearNonEssentialCookies() {
  const cookies = document.cookie.split(';');
  const domains = [window.location.hostname, `.${window.location.hostname}`];

  cookies.forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (!name || ESSENTIAL_COOKIES.includes(name)) return;

    domains.forEach((domain) => {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  });
}

function readStored(): { status: ConsentStatus; categories: ConsentCategories } | null {
  try {
    const v2 = localStorage.getItem(STORAGE_KEY);
    if (v2) {
      const parsed = JSON.parse(v2) as { categories?: Partial<ConsentCategories> } & Partial<ConsentCategories>;
      const cats = parsed?.categories ?? parsed;
      if (cats && (typeof cats.analytics === 'boolean' || typeof cats.marketing === 'boolean')) {
        return {
          status: 'saved',
          categories: {
            necessary: true,
            analytics: !!cats.analytics,
            marketing: !!cats.marketing,
          },
        };
      }
    }

    // Migrate legacy binary consent key so existing users are not re-prompted
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy === 'accepted' || legacy === 'rejected') {
      const migrated: ConsentCategories = {
        necessary: true,
        analytics: legacy === 'accepted',
        marketing: legacy === 'accepted',
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ categories: migrated }));
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      return { status: 'saved', categories: migrated };
    }
  } catch {
    // corrupt value - treat as pending
  }
  return null;
}

export function CookieConsentProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [status, setStatus] = useState<ConsentStatus>('pending');
  const [categories, setCategories] = useState<ConsentCategories>(DEFAULT_CATEGORIES);

  useEffect(() => {
    const stored = readStored();
    if (stored) {
      setStatus(stored.status);
      setCategories(stored.categories);
      applyConsent(stored.categories);
    }
  }, []);

  const applyConsent = (cats: ConsentCategories) => {
    setPosthogAnalyticsConsent(cats.analytics);
    updateGoogleConsent(cats);
    updateMetaConsent(cats.marketing);
  };

  const persist = useCallback((cats: ConsentCategories, previous: ConsentCategories) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ categories: cats }));
    setCategories(cats);
    setStatus('saved');
    applyConsent(cats);
    // Scrub non-essential cookies when a previously-granted category is turned off
    const analyticsTurnedOff = previous.analytics && !cats.analytics;
    const marketingTurnedOff = previous.marketing && !cats.marketing;
    if (analyticsTurnedOff || marketingTurnedOff) {
      clearNonEssentialCookies();
      clearStoredAttribution();
    }
    if (marketingTurnedOff) {
      clearStoredClickIds();
    }
  }, []);

  const acceptAll = useCallback(() => {
    persist({ necessary: true, analytics: true, marketing: true }, categories);
  }, [persist, categories]);

  const rejectAll = useCallback(() => {
    persist({ necessary: true, analytics: false, marketing: false }, categories);
  }, [persist, categories]);

  const savePreferences = useCallback(
    (next: Partial<Omit<ConsentCategories, 'necessary'>>) => {
      persist(
        {
          necessary: true,
          analytics: next.analytics ?? categories.analytics,
          marketing: next.marketing ?? categories.marketing,
        },
        categories,
      );
    },
    [persist, categories],
  );

  const resetConsent = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    setStatus('pending');
    setCategories(DEFAULT_CATEGORIES);
    applyConsent(DEFAULT_CATEGORIES);
    clearNonEssentialCookies();
    clearStoredClickIds();
    clearStoredAttribution();
  }, []);

  const value = useMemo(
    () => ({ status, categories, acceptAll, rejectAll, savePreferences, resetConsent }),
    [status, categories, acceptAll, rejectAll, savePreferences, resetConsent],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}
